import { observer } from "@legendapp/state/react";
import { usePartnerId } from "@simble/shared/hooks/use-partner-id";
import { postPartnersAuth } from "@simble/shared/hooks/use-profile-api";
import { setCookie } from "@simble/shared/lib/cookie-utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import React from "react";
import { getRefreshToken, refreshAuthToken, setRefreshToken } from "@/services/finuslugi-auth";
import { store } from "@/store/local-store";

const tryRestoreSessionByRefresh = async (): Promise<boolean> => {
  if (!getRefreshToken()) {
    return false;
  }
  const newToken = await refreshAuthToken();
  return Boolean(newToken);
};

const PartnerLogin = observer(() => {
  const navigate = useNavigate();
  const search = Route.useSearch();

  const partnerIdMutation = usePartnerId({
    onSuccess: (data) => {
      store.partnerId.set(data.id);
    },
  });
  const processedRef = React.useRef<boolean>(false);

  const mutatePartnerId = React.useCallback(
    async (username: string) => {
      await partnerIdMutation.mutateAsync({
        username,
        channel_id: Number.parseInt(import.meta.env.VITE_PARTNER_CHANNEL_ID || "0", 10),
      });
    },
    [partnerIdMutation.mutateAsync]
  );

  React.useEffect(() => {
    if (processedRef.current) {
      return;
    }

    const processLogin = async () => {
      try {
        processedRef.current = true;

        // Если есть refresh_token — пробуем обновить сессию; при успехе редирект на главную
        if (await tryRestoreSessionByRefresh()) {
          navigate({ to: "/" });
          return;
        }

        const partnerUsername = (search as { partnerUsername: string | number }).partnerUsername;
        const key = (search as { key: string }).key;

        if (!(partnerUsername && key)) {
          console.error("Не удалось получить partnerUsername и/или ключ");
          navigate({ to: "/" });
          return;
        }

        await mutatePartnerId(String(partnerUsername));

        const hasPartnerId = store.partnerId.peek();

        if (!hasPartnerId) {
          console.error("Не удалось получить partnerId");
          navigate({ to: "/" });
          return;
        }

        store.userName.set(partnerUsername.toString());

        const authResponse = await postPartnersAuth("finuslugi");

        setCookie("auth_token", authResponse.token);
        setRefreshToken(authResponse.refresh_token);

        navigate({ to: "/" });
      } catch (error) {
        console.error("Ошибка авторизации партнера:", error);
        navigate({ to: "/" });
      }
    };

    processLogin();
  }, [search, navigate, mutatePartnerId]);

  return null;
});

export const Route = createFileRoute("/partner/login")({
  component: PartnerLogin,
});
